import { useState } from "react";
import type { ChangeEvent, DragEvent } from "react";

interface Props {
  onSubmit: (licenseKey: string) => void | Promise<void>;
  submitLabel?: string;
  busy?: boolean;
  /** Called on every change so a parent (the setup wizard) can hold the text between steps. */
  onTextChange?: (text: string) => void;
  initialText?: string;
}

/** Upload a license file (.lic / .txt) or paste its text. The license is just a long line of text,
 *  so the file is read in the browser and sent as text - nothing is uploaded as a binary. */
export function LicenseUpload({ onSubmit, submitLabel = "Activate license", busy, onTextChange, initialText = "" }: Props) {
  const [text, setText] = useState(initialText);
  const [fileName, setFileName] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [readError, setReadError] = useState<string | null>(null);

  function update(value: string, name: string | null) {
    setText(value);
    setFileName(name);
    onTextChange?.(value);
  }

  async function readFile(file: File | undefined) {
    if (!file) return;
    setReadError(null);
    if (file.size > 20_000) {
      setReadError("That file is too large to be a license - choose the .lic file you received from the vendor.");
      return;
    }
    update((await file.text()).trim(), file.name);
  }

  function handleDrop(e: DragEvent<HTMLLabelElement>) {
    e.preventDefault();
    setDragging(false);
    void readFile(e.dataTransfer.files?.[0]);
  }

  return (
    <div>
      <label
        className={`license-dropzone${dragging ? " license-dropzone-active" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        <input
          type="file"
          accept=".lic,.txt,.key,text/plain"
          style={{ display: "none" }}
          onChange={(e: ChangeEvent<HTMLInputElement>) => void readFile(e.target.files?.[0])}
        />
        <strong>{fileName ? fileName : "Choose your license file"}</strong>
        <span>{fileName ? "Loaded — click to choose a different file" : "or drag & drop the .lic file here"}</span>
      </label>
      {readError && <div className="error-text">{readError}</div>}

      <div className="form-row" style={{ maxWidth: "none", marginTop: 12 }}>
        <label htmlFor="license-text">Or paste the license text</label>
        <textarea
          id="license-text"
          rows={4}
          value={text}
          onChange={(e) => update(e.target.value, null)}
          placeholder="eyJhbGciOiJFZERTQSJ9..."
          style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}
        />
      </div>

      <button
        type="button"
        className="btn-primary"
        disabled={busy || !text.trim()}
        onClick={() => void onSubmit(text)}
      >
        {busy ? "Verifying..." : submitLabel}
      </button>
    </div>
  );
}

/** The installation's ID with a copy button - what the vendor needs to bind a license to this server. */
export function InstallationId({ id }: { id: string | null }) {
  const [copied, setCopied] = useState(false);
  if (!id) return null;
  return (
    <div className="license-copy">
      <code>{id}</code>
      <button
        type="button"
        className="btn-secondary"
        onClick={() => {
          navigator.clipboard?.writeText(id).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
          }).catch(() => undefined);
        }}
      >
        {copied ? "Copied!" : "Copy"}
      </button>
    </div>
  );
}
